import { FileExtensionEnum, GeoJsonTypeEnum, LanguageEnum, ColorEnum } from "../enums";

const NOTIFICATION_DURATION_MS = 3500;
const NOTIFICATION_ERROR_DURATION_MS = 5000;
const NOTIFICATION_DOWNLOADS_DURATION_MS = 5000;
const DEBOUNCE_SEARCH_TIME_MS = 500;
const DEBOUNCE_COPY_PASTE_MS = 250;
const DEBOUNCE_RESIZE_WINDOW_TIME_MS = 50;
const DEBOUNCE_TABLE_SAVE_STATE_TIME_MS = 50;
const DEBOUNCE_TABLE_SHOW_HIDE_NO_RECORDS_TIME_MS = 25;
const DEBOUNCE_SUPPLY_CHAIN_RESIZE_TIME_MS = 50;
const DEBOUNCE_10_MS = 10;

const DATE_FORMAT = "MMM dd, yyyy";
const DATE_TIME_FORMAT = "MMM dd, yyyy - HH:mm";

const TOKEN_CLIENT_ID = "1";

const MAX_DOCUMENT_SIZE_IN_MB = 500;
const MAX_GEO_JSON_SIZE_IN_MB = 1;
const MAX_JSON_SIZE_IN_MB = 1;
const DOCUMENT_UPLOAD_ALLOWED_FILE_EXTENSIONS = [
  FileExtensionEnum.PDF,
  FileExtensionEnum.PNG,
  FileExtensionEnum.JPG,
  FileExtensionEnum.JPEG,
  FileExtensionEnum.DOC,
  FileExtensionEnum.DOCX,
  FileExtensionEnum.XLS,
  FileExtensionEnum.XLSX,
  FileExtensionEnum.PPT,
  FileExtensionEnum.PPTX,
  FileExtensionEnum.CSV,
  FileExtensionEnum.TXT,
  FileExtensionEnum.GEOJSON,
  FileExtensionEnum.JSON,
];
const DOCUMENT_ALLOW_TO_VIEW_FILE_EXTENSIONS = [
  FileExtensionEnum.PDF,
  FileExtensionEnum.PNG,
  FileExtensionEnum.JPG,
  FileExtensionEnum.JPEG,
  FileExtensionEnum.TXT,
  FileExtensionEnum.GEOJSON,
  FileExtensionEnum.JSON,
];

const GEOJSON_UPLOAD_ALLOWED_FILE_EXTENSIONS = [FileExtensionEnum.GEOJSON, FileExtensionEnum.JSON];

const GEO_JSON_VALID_TYPES = [
  GeoJsonTypeEnum.POINT,
  GeoJsonTypeEnum.POLYGON,
  GeoJsonTypeEnum.MULTI_POLYGON,
];

const DEFAULT_NUMBER_PIPE_ROUNDING = "1.0-2";

const AVAILABLE_LANGUAGE_CODES = [LanguageEnum.ENGLISH, LanguageEnum.FRENCH];
const DEFAULT_LANGUAGE_CODE = LanguageEnum.ENGLISH;
const DEFAULT_LANGUAGE_LOCALE_ID = "en-GB";

const MAX_API_GET_ITEMS_SIZE = 2000;
const COMMENTS_MAX_LENGTH = 2000;

const TABLES_AVAILABLE_PAGE_SIZES = [10, 25, 50, 100];
const DEFAULT_TABLES_PAGE_SIZE = TABLES_AVAILABLE_PAGE_SIZES[0];

const CERTIFICATE_DATE_RULES = [
  {
    name: "issuanceDate",
    rules: [
      {
        type: ">",
        target: "validFromDate",
        errorMessage: $localize`:@@constants.cannotbelaterthanvalidfromdate:Cannot be later than Valid from date`,
      },
      {
        type: ">",
        target: "validToDate",
        errorMessage: $localize`:@@constants.cannotbelaterthanvalidtodate:Cannot be later than Valid to date`,
      },
    ],
  },
  {
    name: "validFromDate",
    rules: [
      {
        type: "<",
        target: "issuanceDate",
        errorMessage: $localize`:@@constants.cannotbebeforethanissuancedate:Cannot be before than Issuance date`,
      },
      {
        type: ">",
        target: "validToDate",
        errorMessage: $localize`:@@constants.cannotbelaterthanvalidtodate:Cannot be later than Valid to date`,
      },
    ],
  },
  {
    name: "validToDate",
    rules: [
      {
        type: "<",
        target: "issuanceDate",
        errorMessage: $localize`:@@constants.cannotbebeforethanissuancedate:Cannot be before than Issuance date`,
      },
      {
        type: "<",
        target: "validFromDate",
        errorMessage: $localize`:@@constants.cannotbebeforethanvalidfromdate:Cannot be before than Valid from date`,
      },
    ],
  },
];

const DOCUMENT_DATE_RULES = [
  {
    name: "issuance",
    rules: [
      {
        type: ">",
        target: "validityStart",
        errorMessage: $localize`:@@constants.cannotbelaterthanvalidfromdate:Cannot be later than Valid from date`,
      },
      {
        type: ">",
        target: "validityEnd",
        errorMessage: $localize`:@@constants.cannotbelaterthanvalidtodate:Cannot be later than Valid to date`,
      },
    ],
  },
  {
    name: "validityStart",
    rules: [
      {
        type: "<",
        target: "issuance",
        errorMessage: $localize`:@@constants.cannotbebeforethanissuancedate:Cannot be before than Issuance date`,
      },
      {
        type: ">",
        target: "validityEnd",
        errorMessage: $localize`:@@constants.cannotbelaterthanvalidfromdate:Cannot be later than Valid from date`,
      },
    ],
  },
  {
    name: "validityEnd",
    rules: [
      {
        type: "<",
        target: "validityStart",
        errorMessage: $localize`:@@constants.cannotbebeforethanvalidfromdate:Cannot be before than Valid from date`,
      },
    ],
  },
];

const FULL_SCREEN_PANEL_CLASS = "full-screen-panel-class";
const FULL_SCREEN_WIDTH = () => `${window.innerWidth}px`;
const FULL_SCREEN_HEIGHT = () => `${window.innerHeight - 25}px`;

const MAX_TOOLTIP_LINES_AMOUNT = 10;

const DEFAULT_TAG_COLOUR = ColorEnum.LIGHT_GREY;

const MAX_CHIPS_TEXT_LENGTH_TO_SHOW = 25; // if this value changes, we need to change the value on .max-chip-characters

const MAX_DELIVERY_AGENTS = 5;

const NO_SCROLL_INPUT_TYPES = ["number"];

const MAX_SINGLE_DOCUMENTS_DOWNLOAD = 5;

const ANGULAR_GOOGLE_MAPS_SCRIPT_URL = (key: string, language: LanguageEnum): string =>
  //todo move the key to the config file (all envs)
  `https://maps.googleapis.com/maps/api/js?key=${key}&loading=async&region=GB&language=${language}`;

const AG_GRID_LICENSE_KEY =
  "Using_this_{AG_Grid}_Enterprise_key_{AG-075707}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{iov42_Limited}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Interu}_only_for_{4}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{Interu}_need_to_be_licensed___{Interu}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{30_March_2026}____[v3]_[01]_MTc3NDgyNTIwMDAwMA==71cc94443bf9766fb0b41d9027d30ae0";

export const CommonConstants = {
  NOTIFICATION_DURATION_MS,
  NOTIFICATION_ERROR_DURATION_MS,
  NOTIFICATION_DOWNLOADS_DURATION_MS,
  DEBOUNCE_SEARCH_TIME_MS,
  DEBOUNCE_COPY_PASTE_MS,
  DEBOUNCE_RESIZE_WINDOW_TIME_MS,
  DEBOUNCE_TABLE_SAVE_STATE_TIME_MS,
  DEBOUNCE_TABLE_SHOW_HIDE_NO_RECORDS_TIME_MS,
  DEBOUNCE_SUPPLY_CHAIN_RESIZE_TIME_MS,
  DEBOUNCE_10_MS,
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  TOKEN_CLIENT_ID,
  MAX_DOCUMENT_SIZE_IN_MB,
  MAX_GEO_JSON_SIZE_IN_MB,
  MAX_JSON_SIZE_IN_MB,
  DOCUMENT_UPLOAD_ALLOWED_FILE_EXTENSIONS,
  DOCUMENT_ALLOW_TO_VIEW_FILE_EXTENSIONS,
  GEOJSON_UPLOAD_ALLOWED_FILE_EXTENSIONS,
  GEO_JSON_VALID_TYPES,
  DEFAULT_NUMBER_PIPE_ROUNDING,
  DEFAULT_LANGUAGE_CODE,
  DEFAULT_LANGUAGE_LOCALE_ID,
  AVAILABLE_LANGUAGE_CODES,
  MAX_API_GET_ITEMS_SIZE,
  TABLES_AVAILABLE_PAGE_SIZES,
  DEFAULT_TABLES_PAGE_SIZE,
  CERTIFICATE_DATE_RULES,
  FULL_SCREEN_PANEL_CLASS,
  FULL_SCREEN_WIDTH,
  FULL_SCREEN_HEIGHT,
  MAX_TOOLTIP_LINES_AMOUNT,
  DEFAULT_TAG_COLOUR,
  MAX_CHIPS_TEXT_LENGTH_TO_SHOW,
  COMMENTS_MAX_LENGTH,
  DOCUMENT_DATE_RULES,
  MAX_DELIVERY_AGENTS,
  NO_SCROLL_INPUT_TYPES,
  MAX_SINGLE_DOCUMENTS_DOWNLOAD,
  ANGULAR_GOOGLE_MAPS_SCRIPT_URL,
  AG_GRID_LICENSE_KEY,
};
